# SliderSection.vue
<template>
  <div v-if="!loading" class="slider-container">
    <!-- Swiper -->
    <swiper
      :modules="[Navigation, Pagination, Autoplay, EffectFade]"
      :effect="'fade'"
      :loop="true"
      :speed="1000"
      :autoplay="{
        delay: 5000,
        disableOnInteraction: false,
      }"
      :navigation="{
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      }"
      :pagination="{
        el: '.swiper-pagination',
        clickable: true,
      }"
      class="hero-slider"
    >
      <swiper-slide v-for="slide in slides" :key="slide.id">
        <div class="hero-content" :style="getBackgroundStyle(slide.image)">
          <div class="overlay"></div>
          <div class="container">
            <div class="hero-text" data-aos="fade-up">
              <div class="btn-group">
                <router-link to="/tickets" class="primary-btn">
                  {{ $t('button.buy_tickets') }} <i class="fas fa-ticket ms-2"></i>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </swiper-slide>
    </swiper>

    <!-- Navigation -->
    <div class="swiper-button-next"></div>
    <div class="swiper-button-prev"></div>
    <!-- Pagination -->
    <div class="swiper-pagination"></div>
  </div>
  
  <!-- Loading State -->
  <div v-else class="loading-container">
    <div class="loading-spinner"></div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import axiosInstance from '@/plugins/axios';

const slides = ref([]);
const loading = ref(true);

const getBackgroundStyle = (imageUrl) => {
  return {
    backgroundImage: `url('${imageUrl}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  };
};

const fetchSlides = async () => {
  try {
    const response = await axiosInstance.get('/app-banners');
    console.log('Slides data:', response.data);
    slides.value = response.data;
  } catch (error) {
    console.error('Error fetching slides:', error);
    slides.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchSlides();
});
</script>

<style scoped>
.slider-container {
  position: relative;
  height: 100vh;
  width: 100%;
}

.hero-content {
  height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
}

.overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
}

.hero-text {
  padding: 0 15px;
  color: white;
  text-align: center;
}

.btn-group {
  display: flex;
  gap: 20px;
  justify-content: center;
  animation: slideUp 0.8s ease 0.3s;
}

.primary-btn {
  background: #e4007c;
  color: white;
  padding: 15px 30px;
  border-radius: 30px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.primary-btn:hover {
  background: #cc0070;
  transform: translateY(-3px);
}

/* Loading State */
.loading-container {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1268B1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Swiper Styles */
:deep(.swiper) {
  width: 100%;
  height: 100vh;
}

:deep(.swiper-slide) {
  width: 100%;
  height: 100vh;
}

.swiper-button-next,
.swiper-button-prev {
  color: white;
  background: rgba(255, 255, 255, 0.2);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.swiper-button-next:hover,
.swiper-button-prev:hover {
  background: rgba(255, 255, 255, 0.3);
}

.swiper-button-next::after,
.swiper-button-prev::after {
  font-size: 20px;
}

.swiper-pagination-bullet {
  width: 12px;
  height: 12px;
  background: white;
  opacity: 0.5;
  transition: all 0.3s ease;
}

.swiper-pagination-bullet-active {
  opacity: 1;
  width: 30px;
  border-radius: 6px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .primary-btn {
    padding: 12px 24px;
  }

  .swiper-button-next,
  .swiper-button-prev {
    width: 40px;
    height: 40px;
  }
}

/* RTL Support */
[dir="rtl"] .primary-btn i {
  margin-right: 0;
  margin-left: 8px;
}

[dir="rtl"] .swiper-button-next::after,
[dir="rtl"] .swiper-button-prev::after {
  transform: scaleX(-1);
}
</style>